import React, { useEffect, useState } from 'react';
import { format } from 'date-fns';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { Model } from 'survey-core';

import {
    clearAppointmentAttendanceDetails,
    clearAppointmentsAndSlotsSearch,
    searchAppointmentAttendanceDetails,
    searchAppointmentsAndSlots
} from '../../../../store/calendarService';
import { selectFailedToAttendStatusId } from '../../../../store/dataSelectors';
import {
    loadAttendanceDetails,
    loadServiceDetailsByService
} from '../../../../store/directusService';
import { createFormParticipant, loadServiceConstants } from '../../../../store/participantService';
import { todayDate } from '../../../../utils/dateFunctions';
import {
    APPOINTMENT_ATTENDANCE_ERROR,
    APPOINTMENTS_ERROR,
    SERVICE_CONSTANTS_ERROR,
    SERVICES_ERROR
} from '../../../../utils/formValidation/loadingErrorMessageConstants';
import { sortDateTime } from '../../../../utils/sortFunctions';
import LoadingSpinner from '../../../ui/LoadingSpinner';
import { themeJson } from '../FormTheme';
import GenerateForm from '../GenerateForm';

import UCD603FormTemplate from './UCD603FormTemplate';

const CreateUCD603Form = ({
    currentForm,
    surveyModelData = {},
    participantsAdviser,
    onClose,
    generate = false
}) => {
    const dispatch = useDispatch();

    //LOCAL STATE
    const [fileName, setFileName] = useState('');
    const [surveyModel, setSurveyModel] = useState(new Model('{}'));
    const [isFormComplete, setIsFormComplete] = useState(false);
    const [generatePdf, setGeneratePdf] = useState(false);

    //STORE STATE
    const loggedInUser = useSelector((state) => state.entities.userService.loggedInUser);
    const currentParticipant = useSelector(
        (state) => state.entities.participantService.currentParticipant
    );
    const formStatusDetails = useSelector(
        (state) => state.entities.directusService.formStatusDetails
    );
    const serviceConstants = useSelector(
        (state) => state.entities.participantService.serviceConstants
    );
    const serviceDetailsById = useSelector(
        (state) => state.entities.directusService.serviceDetailsById
    );
    const appointmentAttendanceDetails = useSelector(
        (state) => state.entities.calendarService.appointmentAttendanceDetails
    );
    const failedToAttendStatusId = useSelector(selectFailedToAttendStatusId);
    const appointmentsAndSlotsSearch = useSelector(
        (state) => state.entities.calendarService.appointmentsAndSlotsSearch
    );
    const appointmentsAndSlotsSearchMetaData = useSelector(
        (state) => state.entities.calendarService.appointmentsAndSlotsSearchMetaData
    );
    const loadingAppointmentsAndSlotsSearch = useSelector(
        (state) => state.entities.calendarService.loadingAppointmentsAndSlotsSearch
    );
    const attendanceDetails = useSelector(
        (state) => state.entities.directusService.attendanceDetails
    );
    const successMessage = useSelector((state) => state.entities.formsState.successMessage);

    // USE EFFECTS
    useEffect(() => {
        const fileName = `${currentForm.name}_${format(new Date(), `dd_MMM_yyyy_HH_mm`)}`;
        setFileName(fileName);
        attendanceDetails.length < 1 && dispatch(loadAttendanceDetails());
        return () => cleanUp();
    }, []);

    useEffect(() => {
        if (!failedToAttendStatusId || !('id' in currentParticipant)) return;
        dispatch(
            searchAppointmentAttendanceDetails(
                {
                    participantIds: [currentParticipant.id],
                    statusIds: [failedToAttendStatusId]
                },
                0
            )
        );

        if (!(currentParticipant.serviceId in serviceConstants)) {
            dispatch(loadServiceConstants(currentParticipant.serviceId));
        }
        if (!(currentParticipant.serviceId in serviceDetailsById)) {
            dispatch(loadServiceDetailsByService([currentParticipant.serviceId]));
        }
    }, [currentParticipant, failedToAttendStatusId]);

    useEffect(() => {
        if (!appointmentAttendanceDetails?.length) return;
        const appointmentIds = appointmentAttendanceDetails.map((el) => el.appointmentId);
        appointmentIds.length && dispatch(searchAppointmentsAndSlots({ ids: appointmentIds }, 0));
    }, [appointmentAttendanceDetails]);

    useEffect(() => {
        if (
            !Object.keys(currentForm).length ||
            !appointmentAttendanceDetails?.length ||
            !appointmentsAndSlotsSearchMetaData?.last ||
            loadingAppointmentsAndSlotsSearch ||
            !(currentParticipant.serviceId in serviceConstants) ||
            !(currentParticipant.serviceId in serviceDetailsById) ||
            isFormComplete ||
            !participantsAdviser
        )
            return;

        const constants = serviceConstants[currentParticipant.serviceId];
        const serviceDetails = serviceDetailsById[currentParticipant.serviceId];
        const model = new Model(currentForm.data);
        model.showQuestionNumbers = true;
        model.applyTheme(themeJson);

        model.setValue('Q_1', `${currentParticipant?.firstName} ${currentParticipant?.lastName}`);
        model.setValue('Q_2', currentParticipant?.uniqueIdentifier);

        const failedToAttendAppointments = sortDateTime(appointmentAttendanceDetails).reverse();

        const nonMandatoryAppointmentsMissed = failedToAttendAppointments.filter((el) =>
            appointmentsAndSlotsSearch.find(
                (entry) => entry.id === el.appointmentId && entry.mandatory === false
            )
        );

        const q5Date = nonMandatoryAppointmentsMissed.length
            ? nonMandatoryAppointmentsMissed[0].date
            : '';

        model.setValue('Q_5', q5Date);

        const formattedAddressFromCurrentParticipant = [
            currentParticipant.address1,
            currentParticipant.address2,
            currentParticipant.address3,
            currentParticipant.city,
            currentParticipant.postcode
        ].filter((el) => el);

        model.setValue('Q_7', formattedAddressFromCurrentParticipant.join('\n'));

        const mandatoryAppointmentsMissed = failedToAttendAppointments.filter((el) =>
            appointmentsAndSlotsSearch.find(
                (entry) => entry.id === el.appointmentId && entry.mandatory === true
            )
        );
        const q8DateTime = mandatoryAppointmentsMissed.length
            ? `${mandatoryAppointmentsMissed[0].date}T${mandatoryAppointmentsMissed[0].startTime}:00`
            : '';

        model.setValue('Q_8', q8DateTime);
        model.setValue('Q_15', 'Did not attend');
        model.setValue(
            'Q_19',
            `${participantsAdviser.firstName || ''} ${participantsAdviser.lastName || ''}`
        );
        model.setValue('Q_20', 'Reed in Partnership');
        model.setValue('Q_21', constants.sanctionFormFromEmailAddress);

        const constantsAddressArr = [
            constants.serviceAddressLine1,
            constants.serviceAddressLine2,
            constants.serviceAddressLine3,
            constants.serviceAddressLine4,
            constants.serviceCity,
            constants.serviceCounty,
            constants.servicePostcode
        ].filter((el) => el);

        model.setValue('Q_22', constantsAddressArr.join('\n'));
        model.setValue('Q_23', participantsAdviser?.phoneNumber || '');
        model.setValue('Q_24', serviceDetails.name);
        model.setValue('Q_25', todayDate());

        model.onComplete.add((sender, options) => {
            console.debug('options', options);
            console.debug(JSON.stringify(sender.data, null, 3));
        });
        generate && setSurveyModel(model);
        setIsFormComplete(true);

        const payload = {
            participantId: currentParticipant.id,
            statusId: formStatusDetails.find((el) => el.name === 'Open')?.id || '',
            pdfName: currentForm.name,
            formId: currentForm.id,
            data: { ...surveyModelData, ...model.data }
        };
        dispatch(createFormParticipant(payload));
    }, [
        currentForm,
        appointmentAttendanceDetails,
        appointmentsAndSlotsSearch,
        appointmentsAndSlotsSearchMetaData,
        loadingAppointmentsAndSlotsSearch,
        participantsAdviser,
        serviceConstants,
        serviceDetailsById,
        isFormComplete
    ]);

    useEffect(() => {
        if (successMessage === `Form participant has been created successfully`)
            generate ? setGeneratePdf(true) : onClose();
    }, [successMessage]);

    // HELPER FNS
    const loadingError = () => {
        if (!(currentParticipant.serviceId in serviceConstants)) return SERVICE_CONSTANTS_ERROR;
        if (!(currentParticipant.serviceId in serviceDetailsById)) return SERVICES_ERROR;
        if (Object.keys(appointmentAttendanceDetails)?.length < 1)
            return APPOINTMENT_ATTENDANCE_ERROR;
        if (!appointmentsAndSlotsSearch?.length) return APPOINTMENTS_ERROR;
    };

    const cleanUp = () => {
        dispatch(clearAppointmentAttendanceDetails());
        dispatch(clearAppointmentsAndSlotsSearch());
    };

    // EVENT HANDLERS
    const onFormExit = (isPdfGenerated = false) => onClose(isPdfGenerated);

    const onPdfGenerated = (isPdfGenerated) => {
        setGeneratePdf(false);
        onFormExit(isPdfGenerated);
    };

    // AWAITING CONTENT
    const errorMsg = loadingError();
    if (errorMsg) return <LoadingSpinner content={errorMsg} />;

    // RENDER
    return (
        <div>
            {generate && generatePdf && (
                <GenerateForm
                    fileName={fileName}
                    formCode={currentForm.code}
                    ptCode={currentParticipant.ptCode}
                    userCode={loggedInUser?.code || ''}
                    onPdfGenerated={onPdfGenerated}>
                    <UCD603FormTemplate
                        surveyAnswers={{ ...surveyModelData, ...surveyModel.data }}
                    />
                </GenerateForm>
            )}
        </div>
    );
};

CreateUCD603Form.propTypes = {
    currentForm: PropTypes.object,
    surveyModelData: PropTypes.object,
    participantsAdviser: PropTypes.object,
    onClose: PropTypes.func,
    generate: PropTypes.bool
};

export default CreateUCD603Form;
